import { getAgentHold, holdAgent, resumeAgent } from '../team-hold/teamHold';
import type {
  GfAgentData,
  GfCandidate,
  GfGap,
  GfRecentGap,
} from './types';

/**
 * Gap Filler demo — Alamo Heights Dental, last 30 days (amendment 26).
 *
 * The anchors are the Dashboard's own numbers: **74 gaps filled in 30 days, 4
 * today**. Everything that breaks those down adds back to them — the source
 * table (56+28+18+10 = 112 found, 40+17+10+7 = 74 filled), the channels
 * (50+17+7 = 74 booked), the hero segments ($24,800 + $11,050 + $4,200 + $3,850
 * = $43,900), and today's four fills ($420 + $910 + $1,340 + $1,180 = $3,850).
 */

const c = (
  rank: number,
  name: string,
  contact: string,
  why: string,
  value: string,
  batch: number,
  lastActivity: string,
  status: GfCandidate['status'],
): GfCandidate => ({ id: `c${String(rank)}`, rank, name, contact, why, value, batch, lastActivity, status });

/** The live 2:00 PM gap, in full — the one the detail board is drawn from. */
const TODAY_2PM: GfGap = {
  id: 'g-today-2pm',
  when: 'Today, 2:00 – 3:00 PM',
  source: 'cancelled',
  startsIn: 'starts in 3h 12m',
  provider: 'Dr. Patel',
  operatory: 'Op 3',
  minutes: 60,
  fits: 'cleaning, exam, filling or crown',
  state: 'working',
  funnel: { eligible: 14, offered: 10, replied: 2, booked: 0 },
  footer: 'Batch 2 of 3 · sent 4 min ago · next batch in 16 min · offers expire 1:00 PM',
  batchLine: 'Batch 2 of 3 · sent 4 min ago. Next batch in 16 min, unless someone says yes first.',
  matched: 31,
  couldBeAsked: 14,
  candidates: [
    c(1, 'Sarah Kim', '(210) 555-0182', 'Wants an earlier appointment', '$210', 1,
      'Text 10:24 — “Can I move my Friday one to 3 instead?”', 'needs-a-person'),
    c(2, 'Marcus Toledo', '(210) 555-0143', 'Overdue hygiene · 8 months', '$210', 1,
      'Text 10:31 — “travelling this week”', 'said-no'),
    c(3, 'Grace Winslow', '(210) 555-0197', 'Overdue hygiene · 9 months', '$190', 1,
      'AI call 10:40 — no answer, voicemail left', 'no-reply'),
    c(4, 'Priya Raman', '(210) 555-0169', 'Missed appointment · 8 Sep', '$890', 2,
      'Text 10:44 · delivered', 'sent'),
    c(5, 'Daniel Ostrowski', '(210) 555-0110', 'Unscheduled treatment · crown', '$1,180', 2,
      'Text 10:44 · AI call at 10:54 if no reply', 'sent'),
    c(6, 'Tomás Herrera', 'no mobile · email', 'Unscheduled treatment · filling', '$890', 2,
      'Email 10:44 · sent', 'sent'),
    c(7, 'Eleanor Vance', '(210) 555-0155', 'Overdue hygiene · 11 months', '$190', 3, '—', 'waiting-to-send'),
    c(8, 'Owen Fitzgerald', '(210) 555-0121', 'Overdue hygiene · 7 months', '$190', 3, '—', 'waiting-to-send'),
    c(9, 'Hana Sato', '(210) 555-0138', 'Wants an earlier appointment', '$210', 3, '—', 'waiting-to-send'),
    c(10, 'Luis Moreno', '(210) 555-0164', 'Missed appointment · 2 Sep', '$210', 3, '—', 'waiting-to-send'),
    c(11, 'Bianca Rossi', '(210) 555-0176', 'Overdue hygiene · 6 months', '$190', 4, '—', 'waiting-to-send'),
    c(12, 'Peter Okonkwo', '(210) 555-0188', 'Unscheduled treatment · filling', '$890', 4, '—', 'waiting-to-send'),
    c(13, 'Rosa Delgado', '(210) 555-0190', 'Overdue hygiene · 10 months', '$190', 4, '—', 'waiting-to-send'),
    c(14, 'Kofi Mensah', '(210) 555-0193', 'Wants an earlier appointment', '$210', 4, '—', 'waiting-to-send'),
  ],
  excluded: [
    { count: 6, label: 'Already booked', detail: 'Have an appointment for what they need' },
    { count: 4, label: 'At their message limit', detail: 'Have had enough messages this fortnight' },
    { count: 3, label: 'Work wouldn’t fit', detail: 'Needs longer than 60 minutes' },
    { count: 2, label: 'Opted out', detail: 'Of texts, and no email on file' },
    { count: 2, label: 'No phone or email', detail: 'Nothing to reach them on', fixable: true },
  ],
  next: [
    { at: '10:54', what: 'AI call to Daniel Ostrowski', detail: 'If he hasn’t replied to the text' },
    { at: '11:04', what: 'Batch 3', detail: '4 more patients, by text' },
    { at: '1:00 PM', what: 'Offers expire', detail: 'Anyone who says yes after is told it’s gone' },
  ],
};

const TOMORROW: GfGap = {
  id: 'g-tomorrow-1030',
  when: 'Tomorrow, 10:30 – 11:15 AM',
  source: 'cancelled',
  startsIn: 'starts in 23h 42m',
  provider: 'Dr. Smith',
  operatory: 'Op 1',
  minutes: 45,
  fits: 'filling',
  state: 'paused',
  pausedBy: 'Ava Thompson',
  funnel: { eligible: 8, offered: 5, replied: 1, booked: 0 },
  footer: 'Paused by Ava Thompson, 10:42 AM. Nothing more goes out — its offers still expire at 9:30 AM tomorrow.',
  batchLine: 'Paused by Ava Thompson, 10:42 AM. Nothing more goes out.',
  matched: 12,
  couldBeAsked: 8,
  candidates: [
    c(1, 'Nadia Haddad', '(210) 555-0201', 'Missed appointment · 5 Sep', '$210', 1, 'Text 10:12 · delivered', 'sent'),
    c(2, 'George Bell', '(210) 555-0212', 'Overdue hygiene · 7 months', '$190', 1,
      'Text 10:12 — “yes please!”', 'needs-a-person'),
    c(3, 'Ivy Zhang', '(210) 555-0223', 'Wants an earlier appointment', '$210', 1, 'Text 10:12 · delivered', 'sent'),
    c(4, 'Samuel Reed', '(210) 555-0234', 'Overdue hygiene · 8 months', '$190', 1, 'Text 10:12 · no reply', 'no-reply'),
    c(5, 'Mei Lin', '(210) 555-0245', 'Unscheduled treatment · filling', '$890', 1, 'Text 10:12 · delivered', 'sent'),
  ],
  excluded: [
    { count: 2, label: 'Already booked', detail: 'Have an appointment for what they need' },
    { count: 2, label: 'At their message limit', detail: 'Have had enough messages this fortnight' },
  ],
  next: [
    { at: 'Paused', what: 'Nothing scheduled', detail: 'Resume to start offering again' },
    { at: '9:30 AM tomorrow', what: 'Offers expire', detail: 'The offers already out still expire then' },
  ],
};

const THURSDAY: GfGap = {
  id: 'g-thursday-11',
  when: 'Thursday, 11:00 – 11:30 AM',
  source: 'short-gap',
  startsIn: 'starts in 3 days',
  provider: 'Ava (RDH)',
  operatory: 'Op 2',
  minutes: 30,
  fits: 'cleaning or fluoride',
  state: 'waiting',
  funnel: { eligible: 5, offered: 5, replied: 1, booked: 0 },
  footer: 'Batch 1 of 1 · sent 25 min ago · waiting for replies · offers expire Thursday 10:00 AM',
  batchLine: 'Batch 1 of 1 · sent 25 min ago · waiting for replies.',
  matched: 9,
  couldBeAsked: 5,
  candidates: [
    c(1, 'Leo Martinez', '(210) 555-0301', 'Fluoride due · 9 yrs', '$120', 1, 'Text 9:35 · delivered', 'sent'),
    c(2, 'Amara Okafor', '(210) 555-0312', 'Sealants recommended · 12 yrs', '$140', 1,
      'Text 9:35 — “what time?”', 'needs-a-person'),
    c(3, 'Noah Rezaei', '(210) 555-0323', 'Fluoride due · 7 yrs', '$120', 1, 'Text 9:35 · delivered', 'sent'),
    c(4, 'Mia Chen', '(210) 555-0334', 'Cleaning due · 15 yrs', '$140', 1, 'Text 9:35 · no reply', 'no-reply'),
    c(5, 'Ethan Brooks', '(210) 555-0345', 'Fluoride due · 10 yrs', '$120', 1, 'Text 9:35 · delivered', 'sent'),
  ],
  excluded: [{ count: 4, label: 'At their message limit', detail: 'Have had enough messages this fortnight' }],
  next: [{ at: 'Thursday 10:00 AM', what: 'Offers expire', detail: 'Anyone who says yes after is told it’s gone' }],
};

/** The live gaps, most time-critical first. */
const LIVE_GAPS: GfGap[] = [TODAY_2PM, TOMORROW, THURSDAY];

/* ---- the mutable store --------------------------------------------------- */

/**
 * **One store, so Pause/Resume/We'll-fill-it and the first-yes rule work on mock
 * state** (§0 — on staging every one of these did nothing). The agent cards and
 * the detail page both read it, so a change on one shows on the other.
 */
export const gapStore: { gaps: GfGap[] } = { gaps: LIVE_GAPS.map((g) => ({ ...g })) };

/** Hold, exactly as Recall and Reminders (§2 → amendment 24 §6). */
export const gapHoldStore = { get onHold(): boolean { return getAgentHold('gap-filler').held; } };
export function setGapHold(onHold: boolean): void {
  if (onHold) holdAgent('gap-filler', { by: 'Ava Thompson', since: '9:10 am', waiting: 0 });
  else resumeAgent('gap-filler');
}

export function gapById(id: string): GfGap | undefined {
  return gapStore.gaps.find((g) => g.id === id);
}

function replace(id: string, patch: (g: GfGap) => GfGap): void {
  gapStore.gaps = gapStore.gaps.map((g) => (g.id === id ? patch(g) : g));
}

export function pauseGap(id: string, by: string, at: string): void {
  replace(id, (g) => ({
    ...g,
    state: 'paused',
    pausedBy: by,
    footer: `Paused by ${by}, ${at}. Nothing more goes out — its offers still expire ${expiryPhrase(g)}.`,
    batchLine: `Paused by ${by}, ${at}. Nothing more goes out.`,
  }));
}

export function resumeGap(id: string): void {
  replace(id, (g) => {
    const { pausedBy: _dropped, ...rest } = g;
    return {
      ...rest,
      state: g.funnel.replied > 0 && g.funnel.offered >= g.funnel.eligible ? 'waiting' : 'working',
      footer: 'Resumed — offering again. Next batch shortly.',
      batchLine: 'Resumed — offering again.',
    };
  });
}

/** *We'll fill it ourselves* (§3) — ELVA stops; the list stays to call from. */
export function fillOurselves(id: string): void {
  replace(id, (g) => ({
    ...g,
    state: 'closed',
    outcome: 'filled-by-practice',
    footer: 'You’re filling this one. ELVA has stopped — the list below is yours to call from.',
    batchLine: 'ELVA has stopped on this gap. The list is yours to call from.',
    next: [],
  }));
}

/**
 * **The first yes books it** (§1). The candidate is booked, the funnel's booked
 * box becomes 1, and **every other open offer is told it's taken** — there is no
 * held state, so nothing else can still be in flight.
 */
export function bookGap(id: string, candidateId: string): void {
  replace(id, (g) => {
    const candidates = g.candidates.map((cand) => {
      if (cand.id === candidateId) return { ...cand, status: 'booked' as const };
      // Anyone still in flight is told it's gone; a no / no-reply stays as it was.
      if (cand.status === 'sent' || cand.status === 'needs-a-person' || cand.status === 'waiting-to-send') {
        return { ...cand, status: 'told-its-taken' as const };
      }
      return cand;
    });
    const booked = candidates.find((x) => x.id === candidateId);
    return {
      ...g,
      state: 'closed',
      outcome: 'filled',
      candidates,
      funnel: { ...g.funnel, booked: 1 },
      footer: `Filled — ${booked?.name ?? 'a patient'} said yes. Every other offer was told it’s taken.`,
      batchLine: `Filled — ${booked?.name ?? 'a patient'} said yes first.`,
      next: [],
    };
  });
}

export function skipCandidate(id: string, candidateId: string): void {
  replace(id, (g) => ({
    ...g,
    candidates: g.candidates.filter((cand) => cand.id !== candidateId),
  }));
}

function expiryPhrase(g: GfGap): string {
  const m = /offers expire ([^.]+)/i.exec(g.footer);
  return m?.[1]?.trim() ?? 'at the set time';
}

/** For the agent page — the live gaps, soonest first (already ordered). */
export function liveGaps(): GfGap[] {
  return gapStore.gaps.filter((g) => g.state !== 'closed');
}

/* ---- recent gaps (closed) ------------------------------------------------ */

export const RECENT_GAPS: GfRecentGap[] = [
  { id: 'r1', gap: 'Today 9:15 AM · Dr. Patel · 60 min', source: 'cancelled', outcome: 'filled', result: 'Rachel Moore · text · 12 min', booked: '$420' },
  { id: 'r2', gap: 'Today 8:40 AM · Dr. Smith · 45 min', source: 'no-show', outcome: 'filled', result: 'Marco Bianchi · AI call · 21 min', booked: '$910' },
  { id: 'r3', gap: 'Today 7:50 AM · Dr. Smith · 90 min', source: 'cancelled', outcome: 'filled', result: 'Priya Raman · text · 6 min', booked: '$1,340' },
  { id: 'r4', gap: 'Today 7:30 AM · Dr. Patel · 60 min', source: 'cancelled', outcome: 'filled', result: 'Tom Hughes · text · 15 min', booked: '$1,180' },
  { id: 'r5', gap: 'Yesterday 4:30 PM · Ava (RDH) · 30 min', source: 'never-booked', outcome: 'expired', result: '8 asked, nobody said yes', booked: '—' },
  { id: 'r6', gap: 'Yesterday 3:00 PM · Dr. Patel · 30 min', source: 'cancelled', outcome: 'filled-by-practice', result: 'Paused, then booked at reception', booked: '$190' },
  { id: 'r7', gap: 'Yesterday 11:00 AM · Dr. Smith · 20 min', source: 'short-gap', outcome: 'nobody-to-offer', result: 'Shorter than every rule’s shortest gap', booked: '—' },
];

/**
 * A recent (closed) gap, as a full `GfGap`, so *every recent row opens a detail
 * page* (§2) rather than only the live ones. Read-only, with its outcome in the
 * header.
 */
export function recentGapDetail(id: string): GfGap | undefined {
  const row = RECENT_GAPS.find((r) => r.id === id);
  if (row === undefined) return undefined;
  const [when, provider, len] = row.gap.split(' · ');
  return {
    id: row.id,
    when: when ?? row.gap,
    source: row.source,
    startsIn: 'already started',
    provider: provider ?? '',
    operatory: '',
    minutes: Number(/(\d+)/.exec(len ?? '')?.[1] ?? 0),
    fits: '',
    state: 'closed',
    outcome: row.outcome,
    funnel:
      row.outcome === 'filled'
        ? { eligible: 9, offered: 6, replied: 2, booked: 1 }
        : row.outcome === 'expired'
          ? { eligible: 8, offered: 8, replied: 0, booked: 0 }
          : { eligible: 0, offered: 0, replied: 0, booked: 0 },
    footer: row.result,
    batchLine: row.result,
    matched: 0,
    couldBeAsked: 0,
    candidates: [],
    excluded: [],
    next: [],
  };
}

/* ---- the 30-day dashboard ------------------------------------------------ */

export const GAP_FILLER: GfAgentData = {
  practiceName: 'Alamo Heights Dental',
  titleLead: 'Gap',
  titleAccent: 'Filler',
  subtitle: 'Fills free chair time · Alamo Heights Dental (San Antonio, TX)',
  today: [
    { value: '3', label: 'open now' },
    { value: '4', label: 'filled' },
    { value: '$3,850', label: 'booked' },
    { value: '18 min', label: 'median time to fill' },
  ],
  hero: {
    booked: '$43,900',
    caption: '74 of 112 gaps filled',
    segments: [
      { source: 'cancelled', amount: 24800, value: '$24,800' },
      { source: 'no-show', amount: 11050, value: '$11,050' },
      { source: 'short-gap', amount: 4200, value: '$4,200' },
      { source: 'never-booked', amount: 3850, value: '$3,850' },
    ],
    perGap: '$593',
    chairTime: '62 h',
  },
  tiles: [
    { key: 'found', label: 'Gaps found', value: '112', delta: '▲12%', caption: 'free chair time found or added' },
    { key: 'median', label: 'Median time to fill', value: '18 min', caption: 'from gap found to booked' },
    { key: 'asked', label: 'Patients asked per fill', value: '6.9', caption: 'offers sent per gap filled' },
  ],
  weeks: [
    { label: 'Jul 1', filled: 4 },
    { label: 'Jul 8', filled: 5 },
    { label: 'Jul 15', filled: 5 },
    { label: 'Jul 22', filled: 6 },
    { label: 'Jul 29', filled: 5 },
    { label: 'Aug 5', filled: 6 },
    { label: 'Aug 12', filled: 6 },
    { label: 'Aug 19', filled: 7 },
    { label: 'Aug 26', filled: 6 },
    { label: 'Sep 2', filled: 5 },
    { label: 'Sep 9', filled: 6 },
    { label: 'Sep 16', filled: 7 },
    { label: 'Sep 23', filled: 6 },
  ],
  // 56 + 28 + 18 + 10 = 112 found; 40 + 17 + 10 + 7 = 74 filled.
  sourceRows: [
    { source: 'cancelled', found: 56, filled: 40 },
    { source: 'no-show', found: 28, filled: 17 },
    { source: 'short-gap', found: 18, filled: 10 },
    { source: 'never-booked', found: 10, filled: 7 },
  ],
  // 50 + 17 + 7 = 74 booked.
  channels: [
    { label: 'Text', booked: 50, offers: 420 },
    { label: 'AI call', booked: 17, offers: 110 },
    { label: 'Email', booked: 7, offers: 60 },
  ],
  recent: RECENT_GAPS,
};

/** The Triage banner sentence, only when there is a task (§2). */
export function gapTriageBanner(): { count: number; text: string } | null {
  const needs = gapStore.gaps
    .flatMap((g) => g.candidates.map((cand) => ({ g, cand })))
    .filter((x) => x.cand.status === 'needs-a-person');
  const first = needs[0];
  if (first === undefined) return null;
  return {
    count: needs.length,
    text: `${first.cand.name} asked to move her appointment instead — today’s ${first.g.when.replace(/^Today, /, '').replace(/ –.*/, '')} gap`,
  };
}
